package no.vareflow.app.domain

/**
 * Rules for monthly inventory sessions: when a session may change state, what goes into the
 * closing snapshot, and how progress is counted.
 *
 * Session lifecycle: DRAFT → ACTIVE → CLOSED → ARCHIVED. Only one ACTIVE session per location.
 * Pure functions: callers pass the current data in and apply the result themselves.
 */
object InventoryRules {

    /** Validated input for a new monthly session. */
    data class NewSession(val yearMonth: String, val locationName: String, val locationId: String)

    /**
     * Checks a new session request. Returns null when the month is not `YYYY-MM`, the location is
     * blank, or a non-archived session already exists for the same month and location.
     */
    fun validateNewSession(yearMonth: String, locationName: String, existing: List<InventorySession>): NewSession? {
        val ym = yearMonth.trim()
        val location = locationName.trim()
        if (YearMonthValue.parse(ym) == null || location.isBlank()) return null
        val locationId = locationIdFromName(location)
        val duplicate = existing.any {
            it.yearMonth == ym && it.locationId == locationId && it.status != InventoryStatus.ARCHIVED
        }
        return if (duplicate) null else NewSession(ym, location, locationId)
    }

    /** Users pre-assigned to [category] in a new session: active, non-admin, allowed that category. */
    fun defaultAssignees(category: String, users: List<UserAccount>): Set<String> =
        users.filter { it.active && it.role != Role.ADMIN && category in it.allowedCategories }
            .map { it.id }
            .toSet()

    /** A DRAFT session may start unless another session at the same location is already ACTIVE. */
    fun canActivate(session: InventorySession, all: List<InventorySession>): Boolean {
        if (session.status != InventoryStatus.DRAFT) return false
        return all.none { it.id != session.id && it.locationId == session.locationId && it.status == InventoryStatus.ACTIVE }
    }

    /** Only CLOSED sessions can be archived. */
    fun canArchive(session: InventorySession): Boolean = session.status == InventoryStatus.CLOSED

    /** Counts can only be changed while the session is ACTIVE; closed months are history. */
    fun countsAreEditable(session: InventorySession): Boolean = session.status == InventoryStatus.ACTIVE

    /**
     * An ACTIVE session may close when every category assignment is DONE.
     * A session without any assignments may also close.
     */
    fun canClose(session: InventorySession, sessionAssignments: List<CategoryAssignment>): Boolean {
        // Since v0.8.36 a month can be closed at any time while it is Aktiv (the admin confirms);
        // the Ferdig status per Hovedvaregruppe is no longer used. [sessionAssignments] is kept
        // for callers.
        return session.status == InventoryStatus.ACTIVE
    }

    /**
     * The lines frozen into history when a session closes: one line per counted entry
     * (quantity not null, including 0), with product data copied as it is at closing time.
     * Entries for products that no longer exist are left out. The department name is copied;
     * an unknown department falls back to "Bar".
     */
    fun buildClosingLines(
        sessionId: String,
        entries: List<InventoryCountEntry>,
        products: List<Product>,
        departments: List<InventoryDepartment>
    ): List<CountLineSnapshot> =
        entries.filter { it.sessionId == sessionId && it.quantity != null }.mapNotNull { entry ->
            val product = products.firstOrNull { it.id == entry.productId } ?: return@mapNotNull null
            CountLineSnapshot(
                productId = product.id,
                productName = product.name,
                unit = product.unit,
                pricePerUnit = product.pricePerUnit,
                vat = product.vat,
                quantity = entry.quantity ?: return@mapNotNull null,
                departmentName = departments.firstOrNull { it.sessionId == sessionId && it.id == entry.departmentId }?.name ?: "Bar",
                note = entry.note
            )
        }

    /**
     * The session to show by default: the selected one unless it is archived, otherwise the
     * first ACTIVE, then the first DRAFT, then any session.
     */
    fun pickCurrentSession(selectedId: String?, sessions: List<InventorySession>): InventorySession? {
        val selected = selectedId?.let { id -> sessions.firstOrNull { it.id == id } }
        if (selected != null && selected.status != InventoryStatus.ARCHIVED) return selected
        return sessions.firstOrNull { it.status == InventoryStatus.ACTIVE }
            ?: sessions.firstOrNull { it.status == InventoryStatus.DRAFT }
            ?: sessions.firstOrNull()
    }

    /** Which session to select after the selected one was archived: first ACTIVE, else first DRAFT. */
    fun selectionAfterArchive(sessions: List<InventorySession>): String? =
        sessions.firstOrNull { it.status == InventoryStatus.ACTIVE }?.id
            ?: sessions.firstOrNull { it.status == InventoryStatus.DRAFT }?.id

    /**
     * Number of counted lines (quantity not null; 0 counts, empty does not) for active products,
     * optionally limited to one category. Each department's entry counts separately.
     */
    fun countedCount(
        sessionId: String,
        entries: List<InventoryCountEntry>,
        products: List<Product>,
        category: String? = null
    ): Int {
        val productIds = products.filter { it.active && (category == null || it.category == category) }.map { it.id }.toSet()
        return entries.count { it.sessionId == sessionId && it.productId in productIds && it.quantity != null }
    }

    /** Lines to count: active products (optionally in one category) × departments (at least 1). */
    fun totalToCount(products: List<Product>, category: String? = null, departmentCount: Int = 1): Int {
        val base = products.count { it.active && (category == null || it.category == category) }
        return base * departmentCount.coerceAtLeast(1)
    }
}
